/* Self-contained handler for snippets/product-variant-picker.liquid:
     - variant selection (pills, swatches, dropdowns)
     - quantity-breaks bundles
     - Add to Cart -> opens cart drawer with the selected variant(s)

   Bypasses the theme's existing pub-sub flow (which races with the section
   fetch and leaves the submit button in a stale state) by attaching its
   change listener in the capture phase and unsubscribing the theme handler.

   Include AFTER pubsub.js / global.js. */

(() => {
  const onReady = (cb) =>
    ['interactive', 'complete'].includes(document.readyState)
      ? cb()
      : document.addEventListener('DOMContentLoaded', cb);

  const moneyFmt =
    (window.theme && window.theme.settings && window.theme.settings.money_format) ||
    window.shop_money_format ||
    '${{amount}}';

  const formatMoney = (cents) => {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents, moneyFmt);
    return '$' + (cents / 100).toFixed(2);
  };

  const parseJSON = (el) => {
    try { return el ? JSON.parse(el.textContent) : null; } catch (_) { return null; }
  };

  const cssEscape = (s) =>
    (window.CSS && window.CSS.escape) ? window.CSS.escape(s) : String(s).replace(/(["\\])/g, '\\$1');

  /* ----------------------------- picker reads ----------------------------- */

  const getVariants = (picker) => {
    const all =
      picker.querySelector('script[type="application/json"]:not([data-selected-variant])') ||
      picker.querySelector('script[type="application/json"]');
    return parseJSON(all) || [];
  };

  // For each non-break group, push the selected value (or null if none).
  // Pushing null for empty groups keeps positions aligned with v.options.
  const getSelectedOptions = (picker) => {
    const values = [];
    picker.querySelectorAll('.product-form__input').forEach((group) => {
      if (group.classList.contains('product-form__input--quantity-breaks')) return;
      const radio = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');
      if (radio) values.push(radio.value);
      else if (select && select.name) values.push(select.value);
      else values.push(null);
    });
    return values;
  };

  // Tolerant match: null/empty positions act as wildcards. First variant whose
  // specified positions all match wins. Falls back to variant id from the form
  // input when no value is selected anywhere.
  const findVariantByOptions = (variants, options) => {
    if (!variants.length) return null;
    const hasValue = options.some((v) => v != null && v !== '');
    if (!hasValue) return null;
    return variants.find((v) =>
      options.every((val, i) => val == null || val === '' || v.options[i] === val)
    ) || null;
  };

  // Last-resort: read the variant id Shopify already wrote into the form's
  // hidden input and look it up directly in the variants array.
  const findVariantFromFormId = (sid, variants) => {
    const form = findForm(sid);
    const idInput = form?.querySelector('input[name="id"]');
    const id = parseInt(idInput?.value, 10);
    if (!id) return null;
    return variants.find((v) => v.id === id) || null;
  };

  const sectionId = (picker) => picker.dataset.section;

  const findForm = (sid) =>
    document.getElementById(`product-form-${sid}`) ||
    document.querySelector(`form[id^="product-form-${sid}"]`);

  const findSubmitBtn = (sid) =>
    document.getElementById(`ProductSubmitButton-${sid}`) ||
    findForm(sid)?.querySelector('[type="submit"]');

  const cartHost = () =>
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer');

  /* -------------------------------- updates ------------------------------- */

  const setURL = (picker, variant) => {
    if (!variant || picker.dataset.updateUrl === 'false') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  };

  const setFormVariantId = (sid, variant) => {
    if (!variant) return;
    const form = findForm(sid);
    if (!form) return;
    form.querySelectorAll('input[name="id"]').forEach((i) => {
      i.value = variant.id;
      i.removeAttribute('disabled');
      i.disabled = false;
    });
  };

  const setButtonState = (sid, variant) => {
    const btn = findSubmitBtn(sid);
    if (!btn) return;
    const span = btn.querySelector('span');
    const labels = window.variantStrings || {};
    const available = !!(variant && variant.available);
    if (available) {
      btn.removeAttribute('disabled');
      btn.disabled = false;
      if (span) {
        span.textContent =
          (btn.closest('[data-has-pre-order]') ? labels.preOrder : labels.addToCart) ||
          'Add to cart';
      }
    } else {
      btn.setAttribute('disabled', 'disabled');
      btn.disabled = true;
      if (span) span.textContent = labels.soldOut || 'Sold out';
    }
  };

  const setPrice = (sid, variant) => {
    if (!variant) return;
    const priceEl =
      document.getElementById(`price-${sid}`) ||
      document.querySelector(`#price-${sid} .price__container`) ||
      document.querySelector('.price__container');
    if (!priceEl) return;
    const sale    = priceEl.querySelector('.price-item--sale');
    const regular = priceEl.querySelector('.price-item--regular');
    const compare = priceEl.querySelector('.price-item--last, .price__compare-at, [data-price-compare]');
    const onSale  = variant.compare_at_price && variant.compare_at_price > variant.price;
    const newPrice = formatMoney(variant.price);
    if (sale)    sale.innerHTML    = newPrice;
    if (regular) regular.innerHTML = newPrice;
    if (compare) compare.innerHTML = onSale ? formatMoney(variant.compare_at_price) : '';
    priceEl.classList.toggle('price--on-sale', !!onSale);
  };

  const setDiscountBadge = (root, variant) => {
    if (!variant) return;
    const badge = root.querySelector('.product-card__badge--discount');
    if (!badge) return;
    const onSale = variant.compare_at_price > variant.price;
    badge.classList.toggle('hidden', !onSale);
    if (onSale) {
      const pctEl = badge.querySelector('.percentage');
      if (pctEl) {
        pctEl.textContent = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
      }
    }
  };

  /* ------------------------- quantity-breaks support ---------------------- */

  const getActiveBreak = (picker) => {
    const checked = picker.querySelector('input[type="radio"][name="quantity"]:checked');
    if (!checked) return null;
    const label = picker.querySelector(`label[for="${checked.id}"]`);
    return {
      label,
      quantity: parseInt(label?.dataset.quantity, 10) || 1,
      percentageLeft: parseFloat(label?.dataset.percentageLeft),
      fixedDiscount: parseFloat(label?.dataset.fixedDiscount) || 0
    };
  };

  // Build the cart-add items list:
  //   - no breaks               -> 1x main variant
  //   - break, no per-item picks -> Nx main variant
  //   - break with per-item picks -> aggregated counts per chosen variant
  const buildItems = (picker, variant) => {
    const brk = getActiveBreak(picker);
    if (!brk) return [{ id: variant.id, quantity: 1 }];

    const items = brk.label?.querySelectorAll('.quantity-break__selector-item') || [];
    if (!items.length) return [{ id: variant.id, quantity: brk.quantity }];

    const variants = getVariants(picker);
    const counts = {};
    items.forEach((it) => {
      const opts = Array.from(it.querySelectorAll('select')).map((s) => s.value);
      const v = findVariantByOptions(variants, opts) || variant;
      counts[v.id] = (counts[v.id] || 0) + 1;
    });
    return Object.entries(counts).map(([id, qty]) => ({ id: +id, quantity: qty }));
  };

  // If the active break carries a discount, repaint the price block per-unit.
  const applyBreakPrice = (sid, picker, variant) => {
    const brk = getActiveBreak(picker);
    if (!brk || !variant) return;
    const factor = isNaN(brk.percentageLeft) ? 1 : brk.percentageLeft;
    if (factor >= 1 && brk.fixedDiscount === 0) return;

    const subtotal = variant.price * brk.quantity;
    const discounted = Math.max(0, Math.round(subtotal * factor - brk.fixedDiscount));
    const each = Math.round(discounted / brk.quantity);
    if (each >= variant.price) return;

    const priceEl =
      document.getElementById(`price-${sid}`) ||
      document.querySelector('.price__container');
    if (!priceEl) return;
    const sale    = priceEl.querySelector('.price-item--sale');
    const regular = priceEl.querySelector('.price-item--regular');
    const compare = priceEl.querySelector('.price-item--last, [data-price-compare]');
    if (sale)    sale.innerHTML    = formatMoney(each);
    if (regular) regular.innerHTML = formatMoney(each);
    if (compare) compare.innerHTML = formatMoney(variant.price);
    priceEl.classList.add('price--on-sale');
  };

  /* --------------------------------- submit ------------------------------- */

  const submitAdd = async (picker) => {
    const sid = sectionId(picker);
    const variants = getVariants(picker);
    const variant = findVariantByOptions(variants, getSelectedOptions(picker));
    if (!variant || !variant.available) return;

    const btn = findSubmitBtn(sid);
    btn?.setAttribute('aria-disabled', 'true');
    btn?.classList.add('loading');
    btn?.querySelector('.loading__spinner')?.classList.remove('hidden');

    const target = cartHost();
    const body = { items: buildItems(picker, variant) };
    if (target?.getSectionsToRender) {
      body.sections = target.getSectionsToRender().map((s) => s.section);
      body.sections_url = window.location.pathname;
      target.setActiveElement?.(document.activeElement);
    }

    try {
      const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.status) {
        console.error('add-to-cart error:', data.description || data.message);
        return;
      }
      // renderContents updates the drawer content AND opens it.
      target?.renderContents?.(data);
      if (typeof updateCartCounters === 'function') updateCartCounters();
    } catch (e) {
      console.error('add-to-cart failed', e);
    } finally {
      btn?.removeAttribute('aria-disabled');
      btn?.classList.remove('loading');
      btn?.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  };

  /* --------------------------------- wiring ------------------------------- */

  const refresh = (picker) => {
    const sid = sectionId(picker);
    const variants = getVariants(picker);
    const variant =
      findVariantByOptions(variants, getSelectedOptions(picker)) ||
      findVariantFromFormId(sid, variants);
      console.log("variant",variant)
    if (!variant) {
      setButtonState(sid, null);
      return;
    }
    setURL(picker, variant);
    setFormVariantId(sid, variant);
    setButtonState(sid, variant);
    setPrice(sid, variant);
    setDiscountBadge(picker.closest('product-info') || document, variant);
    applyBreakPrice(sid, picker, variant);
  };

  // Stop the theme's optionValueSelectionChange subscriber so it doesn't
  // race with us by refetching the section and reverting state.
  const detachThemeHandler = () => {
    document.querySelectorAll('product-info').forEach((pi) => {
      if (typeof pi.onVariantChangeUnsubscriber === 'function') {
        try { pi.onVariantChangeUnsubscriber(); } catch (_) {}
        pi.onVariantChangeUnsubscriber = null;
      }
    });
  };

  const attach = (picker) => {
    if (picker.dataset.handlerAttached === '1') return;
    picker.dataset.handlerAttached = '1';

    const sid = sectionId(picker);

    picker.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        const isInput =
          t && (t.tagName === 'SELECT' || (t.tagName === 'INPUT' && t.type === 'radio'));
        if (!isInput) return;
        e.stopImmediatePropagation();

        // Mirror :checked into [checked] so any later DOM read picks it up.
        if (t.tagName === 'INPUT' && t.name) {
          picker
            .querySelectorAll(`input[type="radio"][name="${cssEscape(t.name)}"]`)
            .forEach((r) => {
              if (r === t) r.setAttribute('checked', '');
              else r.removeAttribute('checked');
            });
        }
        refresh(picker);
      },
      true
    );

    const form = findForm(sid);
    if (form && form.dataset.handlerAttached !== '1') {
      form.dataset.handlerAttached = '1';
      form.addEventListener(
        'submit',
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          submitAdd(picker);
        },
        true
      );
    }

    refresh(picker);
  };

  const boot = () => {
    detachThemeHandler();
    document.querySelectorAll('product-variant-selects').forEach(attach);
  };

  onReady(boot);
  document.addEventListener('shopify:section:load', boot);
})();
